import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { catalogApi } from '@/api/catalogApi'

/**
 * Superadmin catalog queries and mutations. Every write refreshes the admin
 * lists, the admin audit and the ticket pickers (`products` / `customers`),
 * so a new product, client or tenant shows up in the New ticket dialog.
 */
function useRefresh() {
  const queryClient = useQueryClient()
  return () => {
    for (const key of [['catalog'], ['admin', 'audit'], ['products'], ['customers']])
      void queryClient.invalidateQueries({ queryKey: key })
  }
}

export function useCatalogProducts(enabled = true) {
  return useQuery({ queryKey: ['catalog', 'products'], queryFn: catalogApi.products, enabled, retry: false })
}

export function useCatalogProduct(id: string | undefined) {
  return useQuery({ queryKey: ['catalog', 'product', id], queryFn: () => catalogApi.product(id!), enabled: !!id, retry: false })
}

export function useCatalogClients(q: string, enabled = true) {
  return useQuery({ queryKey: ['catalog', 'clients', q], queryFn: () => catalogApi.clients(q), enabled, retry: false })
}

export function useCatalogClient(id: string | undefined) {
  return useQuery({ queryKey: ['catalog', 'client', id], queryFn: () => catalogApi.client(id!), enabled: !!id, retry: false })
}

export function useCatalogTenants(clientId: string | undefined) {
  return useQuery({ queryKey: ['catalog', 'tenants', clientId], queryFn: () => catalogApi.tenants(clientId!), enabled: !!clientId, retry: false })
}

/** Wraps any catalog API call as a mutation that refreshes everything catalog-related. */
export function useCatalogMutation<A, R>(fn: (args: A) => Promise<R>) {
  const refresh = useRefresh()
  return useMutation({ mutationFn: fn, onSuccess: refresh })
}
